/**
 * Configuration Initialization Hook
 * Loads runtime configuration when the app starts
 */

import { useEffect, useState } from 'react';
import { loadRuntimeConfig, type RuntimeConfig } from '../lib/runtimeConfig';

interface UseRuntimeConfigResult {
  config: RuntimeConfig | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to load and manage runtime configuration
 * @returns Configuration state and loading status
 */
export function useRuntimeConfig(): UseRuntimeConfigResult {
  const [config, setConfig] = useState<RuntimeConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isCancelled = false;

    const loadConfig = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const runtimeConfig = await loadRuntimeConfig();
        
        if (!isCancelled) {
          setConfig(runtimeConfig);
        }
      } catch (err) {
        if (!isCancelled) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to load configuration';
          setError(errorMessage);
          console.error('Configuration loading error:', err);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    loadConfig();

    // Cleanup function to cancel if component unmounts
    return () => {
      isCancelled = true;
    };
  }, []);

  return { config, isLoading, error };
}
